'use client';

import { Box, Heading } from '@chakra-ui/react';
import { Flex } from '@chakra-ui/react';
import { ImageProps } from 'next/image';
import Image from 'next/image';
import React, { useState } from 'react';
import slugify from 'react-slugify';
import { Autoplay, EffectFade, Navigation, Pagination } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';

import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import 'swiper/css/effect-fade';
import './HeroBanner.scss';
import ButtonCustom from '@/app/_components/button/ButtonCustom';
import { useOpenFloatingCTA } from '@/hooks/floating-cta.hook';
import { directusApiUri } from '@/services/api.service';
import type { ImageTransformationKeys } from '@/types/files/files.types';
import type { NewCarData } from '@/types/new-cars/new-cars.types';
import imageLoader from '@/utils/image-loaders';

import LinkCustom from '../link/LinkCustom';

import styles from './HeroBanner.module.scss';

type HeroBannersT = {
  desktop_image: string;
  mobile_image: string;
  terms_and_conditions: string;
  heading: string;
  subheading: string;
  page: string;
  show_call_to_action: boolean;
  call_to_action_text: string;
  call_to_action_url: string;

  show_call_to_action_2: boolean;
  call_to_action_text_2: string;
  call_to_action_url_2: string;

  show_book_test_drive: boolean;
  test_drive_range: { name: string };
};

type Props = {
  imageKeys: ImageTransformationKeys;
  heroBanners: HeroBannersT[];
  newCars: NewCarData[];
};

const HeroBanner = React.memo(({ heroBanners, newCars }: Props) => {
  const openLeadForm = useOpenFloatingCTA();

  const [currentItem, setCurrentItem] = useState(heroBanners[0]);

  const renderItem = (item: HeroBannersT, index: number) => {
    return (
      <Box className="heroBannerContentWrapper">
        <Box className="heroBannerContent">
          <Box className="headingElement">
            <Heading
              as={index === 0 ? 'h1' : 'h2'}
              className="titleXl"
              marginBottom={{ base: 4, md: 8 }}
            >
              {item.heading}
            </Heading>
            <Flex alignItems="center" gap="4">
              {item.show_call_to_action && item.call_to_action_text ? (
                <LinkCustom
                  className="btnWhite"
                  moduleClass="btnCta"
                  href={item?.call_to_action_url}
                >
                  {item?.call_to_action_text}
                </LinkCustom>
              ) : null}

              {item?.show_call_to_action_2 && item.call_to_action_text_2 ? (
                <LinkCustom
                  className="btnOutlineWhite"
                  moduleClass="btnCta"
                  href={item?.call_to_action_url_2}
                >
                  {item?.call_to_action_text_2}
                </LinkCustom>
              ) : null}

              {item?.show_book_test_drive && (
                <ButtonCustom
                  btnText="Book A Test Drive"
                  className="btnOutlineWhite"
                  moduleClass="btnCta"
                  onClick={() => openTestDriveModal(item)}
                />
              )}
            </Flex>
          </Box>
        </Box>
      </Box>
    );
  };

  const openTestDriveModal = (item: HeroBannersT) => {
    const newCar = newCars?.find(
      (c) => slugify(c.main_title) === slugify(item.test_drive_range.name),
    );

    if (!newCar) {
      return;
    }

    openLeadForm('test-drive', 'TestDrive', {
      type: 'new-car',
      newCar: newCar,
    });
  };

  return (
    <>
      <Swiper
        effect={'fade'}
        autoplay={{
          delay: 20000,
          disableOnInteraction: true,
        }}
        loop={true}
        navigation={true}
        pagination={{ clickable: true }}
        modules={[EffectFade, Navigation, Pagination, Autoplay]}
        breakpoints={{
          0: {
            slidesPerView: 1,
            spaceBetween: 20,
          },
          768: {
            slidesPerView: 1,
            spaceBetween: 40,
          },
          1024: {
            slidesPerView: 1,
            spaceBetween: 50,
          },
        }}
        className="heroSwiper"
        onSlideChange={(s) => setCurrentItem(heroBanners[s.realIndex])}
      >
        {heroBanners &&
          heroBanners?.map((item, index) => {
            const bannerId = `banner-${index}-${item.desktop_image || ''}`;
            const qualityToRender = 90;
            const quality = index === 0 ? 30 : qualityToRender;

            const onLoad = (event: any) => {
              const text = `image-q${quality}`;
              const textToReplace = `image-q${qualityToRender}`;

              if (!(event.currentTarget.src || '').includes(text)) {
                return;
              }

              const newSrc = event.currentTarget.src.replace(
                text,
                textToReplace,
              );
              const newSrcSet = event.currentTarget.srcset.replaceAll(
                text,
                textToReplace,
              );

              event.currentTarget.srcset = newSrcSet;
              event.currentTarget.src = newSrc;
            };

            const imageProps: ImageProps = {
              src: `${directusApiUri}/assets/${item.desktop_image}?__dis=${item.desktop_image}&__mis=${item.mobile_image}`,
              alt: item.desktop_image,
              fill: true,
              placeholder: 'empty',
              priority: index === 0,
              quality,
              loader: imageLoader,
              id: bannerId,
              sizes: '(max-width: 575px) 30vw',
            };

            if (index === 0) {
              imageProps.onLoad = onLoad;
            }

            return (
              <SwiperSlide key={index}>
                <div className="heroSwiperWrap">
                  <div className={styles.bannerImage}>
                    <Image {...imageProps} alt={item.mobile_image} />
                  </div>
                  {renderItem(item, index)}
                </div>
              </SwiperSlide>
            );
          })}
      </Swiper>
      <Box className="headingElementMobile">
        <Box
          className="headingElement"
          paddingTop={'64px'}
          paddingBottom={{ base: '33px', md: 12 }}
          paddingX={6}
        >
          {currentItem?.heading ? (
            <Heading
              as={currentItem === heroBanners[0] ? 'h1' : 'h2'}
              className="titleXl"
              marginBottom={{ base: 8 }}
            >
              {currentItem?.heading}
            </Heading>
          ) : null}
          {currentItem?.show_call_to_action &&
            currentItem?.call_to_action_text && (
              <LinkCustom
                className="btnWhite"
                href={currentItem?.call_to_action_url}
                moduleClass="btnCta"
                extraClass="btnFirst"
              >
                {currentItem?.call_to_action_text?.trim()}
              </LinkCustom>
            )}
          {currentItem?.show_book_test_drive && (
            <ButtonCustom
              btnText="Book A Test Drive"
              className="btnOutlineWhite"
              moduleClass="btnCta"
              onClick={() => openTestDriveModal(currentItem)}
            />
          )}
          {currentItem?.show_call_to_action_2 &&
            currentItem?.call_to_action_text_2 && (
              <>
                <LinkCustom
                  className="btnWhite"
                  moduleClass="btnCta"
                  href={currentItem.call_to_action_url_2}
                >
                  {currentItem?.call_to_action_text_2}
                </LinkCustom>
              </>
            )}
        </Box>
      </Box>
    </>
  );
});

HeroBanner.displayName = 'HeroBanner';

export default HeroBanner;
